<template>
  <div class="pl-2 pr-2">
    <h1>Admin Export</h1>

    <!-- Update End Time for All Items (active only) -->
    <div class="grid">
      <Card class="col-12 col-lg-6">
        <template #header
          ><h5>Update End Time for <strong>Active Items</strong></h5></template
        >
        <template #content>
          <label for="enddate">Select New End Date:</label>
          <VueDatePicker
            v-model="selectedEndDate"
            time-picker-inline
            dateFormat="yy-mm-dd"
          />
        </template>
        <template #footer>
          <argon-button
            type="button"
            variant="gradient"
            class="ms-auto btn-success"
            @click="updateEndTimeForAllItems"
            >Update End Time for Active Items</argon-button
          >
          <p class="mt-3">
            Note: This will not update auction items that have ended or items
            with Buy Now bids.
          </p>
        </template>
      </Card>
    </div>

    <!-- Update End Time for ALL Items (including ended) -->
    <div class="grid mt-3">
      <Card class="col-12 col-lg-6">
        <template #header
          ><h5>Set End Date for <strong>ALL Items</strong> (including ended)</h5></template
        >
        <template #content>
          <p class="text-muted mb-3">Sets the end date on every auction item regardless of current status. Use this to reset the auction timeline.</p>
          <label for="enddateAll">Select New End Date:</label>
          <VueDatePicker
            v-model="selectedEndDateAll"
            time-picker-inline
            dateFormat="yy-mm-dd"
          />
        </template>
        <template #footer>
          <argon-button
            type="button"
            class="ms-auto btn-warning"
            @click="updateEndTimeForAllItemsForced"
            >Set End Date for ALL Items</argon-button
          >
          <p class="mt-3">
            Note: This updates <strong>all</strong> items, including those that have already ended and Live Auction items.
          </p>
        </template>
      </Card>
    </div>
    <!-- Manual Donation Component -->
    <ManualDonation />

    <!-- Reset All Items Component -->
    <ResetAllItems />
    <!-- Export All Items as CSV -->

    <div class="grid">
      <Card class="col-12 col-lg-6 mt-5">
        <template #header><h5>Export All Items as CSV</h5></template>
        <template #content>
          <argon-button
            type="button"
            variant="gradient"
            class="ms-auto btn-success"
            @click="exportAuctionItems"
            >Export Auction Items</argon-button
          >
        </template>
      </Card>
    </div>

    <!-- Export Sold Items and Donations as CSV -->
    <div class="grid">
      <Card class="col-12 col-lg-6 mt-5">
        <template #header
          ><h5>Export Sold Items and Donations as CSV</h5></template
        >
        <template #content>
          <argon-button
            type="button"
            variant="gradient"
            class="ms-auto btn-success"
            @click="exportPurchasedItems"
            >Export Sold Items and Donations</argon-button
          >
        </template>
      </Card>
    </div>

    <!-- Enable/Disable All Users -->
    <div class="grid">
      <Card class="col-12 col-lg-6 mt-5">
        <template #header
          ><h5>Enable/Disable All Users (Except Admins)</h5></template
        >
        <template #content>
          <div class="grid">
            <div class="col-6">
              <argon-button
                type="button"
                class="ms-auto btn-danger"
                @click="updateUserStatus(false)"
                >Disable All Users
              </argon-button>
            </div>
            <div class="col-6">
              <argon-button
                type="button"
                variant="gradient"
                class="ms-auto btn-success"
                @click="updateUserStatus(true)"
                >Enable All Users</argon-button
              >
            </div>
          </div>
        </template>
      </Card>
    </div>

    <!-- Password Requirement Toggle -->
    <div class="grid mt-5">
      <Card class="col-12 col-lg-6">
        <template #header><h5>Password Requirement for User Login</h5></template>
        <template #content>
          
          
    <ToggleButton
            id="requirePassword"
            v-model="requirePassword"
            @change="togglePasswordRequirement"
            onLabel="Use password"
            offLabel="Do not use password"
            onIcon="pi pi-lock"
            offIcon="pi pi-lock-open"
            class="w-36"
            aria-label="Do you confirm"
          />
          
        </template>
      </Card>
    </div>

    <!-- Kiosk Idle Timeout Setting -->
    <div class="grid mt-5">
      <Card class="col-12 col-lg-6">
        <template #header><h5>Kiosk: Auto sign-out on inactivity</h5></template>
        <template #content>
          <p class="text-muted mb-3">Enter the number of seconds before an inactive user is automatically signed out. Set to 0 to disable auto sign-out.</p>
          <div class="d-flex align-items-center gap-3">
            <input
              type="number"
              v-model.number="idleTimeoutSeconds"
              min="0"
              step="30"
              class="form-control"
              style="max-width: 140px;"
              placeholder="Seconds"
            />
            <span class="text-muted">seconds ({{ idleTimeoutDisplay }})</span>
          </div>
        </template>
        <template #footer>
          <argon-button
            type="button"
            variant="gradient"
            class="ms-auto btn-success"
            @click="saveIdleTimeout"
          >Save</argon-button>
        </template>
      </Card>
    </div>

    <!-- Swipe Navigation Toggle -->
    <div class="grid mt-5">
      <Card class="col-12 col-lg-6">
        <template #header><h5>Swipe Navigation Between Items</h5></template>
        <template #content>
          <p class="text-muted mb-3">Allow users to swipe left/right to navigate between auction items.</p>
          <ToggleButton
            id="swipeNavigation"
            v-model="swipeNavigationEnabled"
            @change="toggleSwipeNavigation"
            onLabel="Swipe enabled"
            offLabel="Swipe disabled"
            onIcon="pi pi-arrow-right-arrow-left"
            offIcon="pi pi-ban"
            class="w-36"
          />
        </template>
      </Card>
    </div>

    <!-- Summary 2 Display Settings -->
    <div class="grid mt-5">
      <Card class="col-12 col-lg-6">
        <template #header><h5>TV Display: Summary (Gala) Settings</h5></template>
        <template #content>
          <p class="text-muted mb-3">
            Control what is visible on the <strong>Display 6 – Summary (Gala)</strong> screen.
            Hide "Total Raised" if the amount should not be shown publicly.
          </p>
          <ToggleButton
            id="showTotalRaised"
            v-model="showTotalRaised"
            @change="toggleShowTotalRaised"
            onLabel="Total Raised visible"
            offLabel="Total Raised hidden"
            onIcon="pi pi-eye"
            offIcon="pi pi-eye-slash"
            class="w-36"
          />
        </template>
      </Card>
    </div>
  </div>
</template>

<script>
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  where,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "@/firebase"; // Adjust the import path based on your project structure
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import Card from "primevue/card";
import ManualDonation from "@/views/admin/components/ManualDonation.vue";
import ResetAllItems from "@/views/admin/components/ResetAllItems.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import { ref, computed, onMounted } from "vue";
import { useToast } from "vue-toastification";
import ToggleButton from 'primevue/togglebutton';
import { useStore } from "vuex";


export default {
  name: "AdminExport",
  components: {
    VueDatePicker,
    Card,
    ManualDonation,
    ArgonButton,
    ResetAllItems,
    ToggleButton,
  },
  setup() {
  const toast = useToast();
  const store = useStore();
  const requirePassword = ref(store.getters.requirePassword); // Use Vuex getter

  // --- Kiosk idle timeout ---
  const idleTimeoutSeconds = ref(300); // default 5 min

  const idleTimeoutDisplay = computed(() => {
    const s = idleTimeoutSeconds.value;
    if (!s || s <= 0) return 'disabled';
    const min = Math.floor(s / 60);
    const sec = s % 60;
    if (min && sec) return `${min} min ${sec} sec`;
    if (min) return `${min} min`;
    return `${sec} sec`;
  });

  const fetchIdleTimeout = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, "auctionSettings", "kioskSettings"));
      if (settingsDoc.exists() && settingsDoc.data().idleTimeoutSeconds !== undefined) {
        idleTimeoutSeconds.value = settingsDoc.data().idleTimeoutSeconds;
      } else {
        await setDoc(doc(db, "auctionSettings", "kioskSettings"), {
          idleTimeoutSeconds: 300,
        });
        idleTimeoutSeconds.value = 300;
      }
    } catch (error) {
      console.error("Error fetching kiosk idle timeout:", error);
    }
  };

  const saveIdleTimeout = async () => {
    try {
      await setDoc(doc(db, "auctionSettings", "kioskSettings"), {
        idleTimeoutSeconds: idleTimeoutSeconds.value,
      }, { merge: true });
      toast.success(`Auto sign-out saved: ${idleTimeoutDisplay.value}`);
    } catch (error) {
      console.error("Error saving kiosk idle timeout:", error);
      toast.error("Could not save setting.");
    }
  };

  // Fetch the password requirement status from Firestore
  const fetchPasswordRequirement = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, "auctionSettings", "passwordLogin"));
        if (settingsDoc.exists()) {
          requirePassword.value = settingsDoc.data().requirePassword;
        } else {
          await setDoc(doc(db, "auctionSettings", "passwordLogin"), {
            requirePassword: true,
          });
          requirePassword.value = true;
        }
      } catch (error) {
        console.error("Error fetching password requirement status:", error);
        toast.error("Failed to fetch password requirement status.");
      }
    };

   // Toggle the password requirement
   const togglePasswordRequirement = async () => {
      const newRequirement = requirePassword.value;
      
      try {
        await updateDoc(doc(db, "auctionSettings", "passwordLogin"), {
          requirePassword: newRequirement,
        });
        requirePassword.value = newRequirement;
        toast.success(`Password requirement is now ${newRequirement ? 'enabled' : 'disabled'}`);
      } catch (error) {
        console.error("Error updating password requirement:", error);
        toast.error("Failed to update password requirement.");
      }
    };

  // --- Swipe Navigation ---
  const swipeNavigationEnabled = ref(true);

  const fetchSwipeNavigation = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, "auctionSettings", "uiSettings"));
      if (settingsDoc.exists() && settingsDoc.data().swipeNavigation !== undefined) {
        swipeNavigationEnabled.value = settingsDoc.data().swipeNavigation;
      }
    } catch (error) {
      console.error("Error fetching swipe navigation setting:", error);
    }
  };

  const toggleSwipeNavigation = async () => {
    try {
      await setDoc(doc(db, "auctionSettings", "uiSettings"), {
        swipeNavigation: swipeNavigationEnabled.value,
      }, { merge: true });
      toast.success(`Swipe navigation ${swipeNavigationEnabled.value ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error("Error saving swipe navigation setting:", error);
      toast.error("Could not save setting.");
    }
  };

  // --- Summary 2: show/hide Total Raised ---
  const showTotalRaised = ref(true);

  const fetchShowTotalRaised = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, "auctionSettings", "displaySettings"));
      if (settingsDoc.exists() && settingsDoc.data().showTotalRaised !== undefined) {
        showTotalRaised.value = settingsDoc.data().showTotalRaised;
      }
    } catch (error) {
      console.error("Error fetching showTotalRaised setting:", error);
    }
  };

  const toggleShowTotalRaised = async () => {
    try {
      await setDoc(doc(db, "auctionSettings", "displaySettings"), {
        showTotalRaised: showTotalRaised.value,
      }, { merge: true });
      toast.success(`Total Raised is now ${showTotalRaised.value ? 'visible' : 'hidden'} on the Gala Summary screen`);
    } catch (error) {
      console.error("Error saving showTotalRaised setting:", error);
      toast.error("Could not save setting.");
    }
  };

  // Call fetch functions on component mount
  onMounted(() => {
    fetchPasswordRequirement();
    fetchIdleTimeout();
    fetchSwipeNavigation();
    fetchShowTotalRaised();
  });

  return {
    requirePassword,
    togglePasswordRequirement,
    idleTimeoutSeconds,
    idleTimeoutDisplay,
    saveIdleTimeout,
    swipeNavigationEnabled,
    toggleSwipeNavigation,
    showTotalRaised,
    toggleShowTotalRaised,
  };
  },
  data() {
    return {
      selectedEndDate: null, // Holds the selected end date (active items only)
      selectedEndDateAll: null, // Holds the selected end date for ALL items
    };
  },
  async mounted() {
  },
  methods: {
    // Fetch users from Firestore
    async fetchUsers() {
      try {
        const usersCollection = collection(db, "users");
        const usersSnapshot = await getDocs(usersCollection);
        return usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (error) {
        console.error("Error fetching users:", error);
        return [];
      }
    },

    // Update user status (enable/disable)
    async updateUserStatus(isEnabled) {
      try {
        const users = await this.fetchUsers();
        const usersToUpdate = users.filter((user) => !user.userAdmin);

        const batchPromises = usersToUpdate.map(async (user) => {
          const userRef = doc(db, "users", user.id);
          await updateDoc(userRef, {
            userEnabled: isEnabled, // Set `userEnabled` to `true` or `false`
          });
        });

        await Promise.all(batchPromises);
        alert(
          `All users have been ${isEnabled ? "enabled" : "disabled"} successfully.`
        );
      } catch (error) {
        console.error("Error updating user status:", error);
        alert("An error occurred while updating user status.");
      }
    },

    // Fetch all auction items from Firestore
    async fetchAuctionItems() {
      try {
        const auctionItemsCollection = collection(db, "auctionItems");
        const auctionItemsSnapshot = await getDocs(auctionItemsCollection);
        return auctionItemsSnapshot.docs.map((doc) => ({
          autoID: doc.id, // Auto-generated document ID
          ...doc.data(), // All other fields from the document
        }));
      } catch (error) {
        console.error("Error fetching auction items:", error);
        return [];
      }
    },

    // Fetch donation items from Firestore
    async fetchDonations() {
      try {
        const auctionItemsCollection = collection(db, "auctionItems");
        const donationItemsQuery = query(
          auctionItemsCollection,
          where("isDonationItem", "==", true)
        );
        const donationItemsSnapshot = await getDocs(donationItemsQuery);
        const donationsList = [];

        for (const itemDoc of donationItemsSnapshot.docs) {
          const purchasesCollection = collection(
            db,
            `auctionItems/${itemDoc.id}/purchase`
          );
          const purchasesSnapshot = await getDocs(purchasesCollection);

          purchasesSnapshot.forEach((purchaseDoc) => {
            donationsList.push({
              ...purchaseDoc.data(),
              itemID: itemDoc.id,
            });
          });
        }
        return donationsList;
      } catch (error) {
        console.error("Error fetching donation items:", error);
        return [];
      }
    },

    // Export sold items and donations as a CSV file
    async exportPurchasedItems() {
      try {
        const users = await this.fetchUsers();
        const items = await this.fetchAuctionItems();
        const donations = await this.fetchDonations();

        const soldItems = items.filter(
          (item) =>
            item.enddate.seconds * 1000 < Date.now() && !item.isDonationItem
        );

        const csvRows = [
          [
            "Item Type",
            "Item Number/Name",
            "Bid Amount",
            "Bidder Name",
            "Bidder ID",
            "Assigned Table",
            "Email",
            "Mobile",
          ].join(","),
        ];

        for (const item of soldItems) {
          const user =
            users.find((user) => user.id === item.latestBidderID) || {};
          csvRows.push(
            [
              "Auction Item",
              item.itemNumber,
              item.latestBid,
              item.latestBidder,
              item.latestBidderID,
              user.assignedTableName || "",
              user.email || "",
              user.mobile || "",
            ].join(",")
          );
        }

        for (const donation of donations) {
          const user =
            users.find((user) => user.id === donation.user.uid) || {};
          csvRows.push(
            [
              "Donation Item",
              donation.itemName,
              donation.userBid,
              donation.user.hiddenName || donation.user.name || "Anonymous",
              donation.user.uid,
              user.assignedTableName || donation.user.table || "",
              user.email || "",
              donation.user.mobile || user.mobile || "",
            ].join(",")
          );
        }

        const csvContent = csvRows.join("\n");
        this.downloadCSV(csvContent, "purchased_items.csv");
      } catch (error) {
        console.error("Error exporting purchased items:", error);
      }
    },

    // Convert items to CSV format
    convertToCSV(items) {
      const headers = [
        "itemNumber",
        "name",
        "creator",
        "startbid",
        "estimatedValue",
        "autoID",
        "itemURL",
      ];
      const csvRows = [headers.join(",")]; // CSV headers

      items.forEach((item) => {
        const itemPath = item.isDonationItem === true ? "donationitem" : "item";
        const itemURL = `https://theperfectauction.com/auction/${itemPath}/${item.autoID}`;

        const escapeCsvValue = (value) => {
          if (typeof value === "string" && value.includes(",")) {
            return `"${value.replace(/"/g, '""')}"`; // Escape double quotes within the string
          }
          return value;
        };

        const row = [
          escapeCsvValue(item.itemNumber || ""),
          escapeCsvValue(item.name || ""),
          escapeCsvValue(item.creator || ""),
          escapeCsvValue(item.startbid || ""),
          escapeCsvValue(item.estimatedValue || ""),
          escapeCsvValue(item.autoID || ""),
          escapeCsvValue(itemURL || ""),
        ];
        csvRows.push(row.join(","));
      });

      return csvRows.join("\n");
    },

    // Trigger download of the CSV file
    downloadCSV(csvContent, fileName) {
      // Prepend UTF-8 BOM so Excel on Windows decodes non-ASCII characters correctly.
      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // Export all auction items as a CSV file
    async exportAuctionItems() {
      try {
        const items = await this.fetchAuctionItems();
        if (items.length === 0) {
          console.warn("No items found to export.");
          return;
        }
        const csvContent = this.convertToCSV(items);
        this.downloadCSV(csvContent, "auction_items.csv");
      } catch (error) {
        console.error("Error exporting auction items:", error);
      }
    },

    // Update end time for ALL items regardless of status
    async updateEndTimeForAllItemsForced() {
      if (!this.selectedEndDateAll) {
        alert("Please select a new end date.");
        return;
      }
      if (!confirm("This will update ALL auction items including those that have already ended. Are you sure?")) {
        return;
      }
      try {
        const auctionItems = await this.fetchAuctionItems();
        const batchPromises = auctionItems.map(async (item) => {
          const itemRef = doc(db, "auctionItems", item.autoID);
          await updateDoc(itemRef, {
            enddate: this.selectedEndDateAll,
          });
        });
        await Promise.all(batchPromises);
        alert(`End time updated for all ${auctionItems.length} items.`);
      } catch (error) {
        console.error("Error updating end times:", error);
        alert("An error occurred while updating end times.");
      }
    },

    // Update the end time for all applicable auction items
    async updateEndTimeForAllItems() {
      if (!this.selectedEndDate) {
        alert("Please select a new end date.");
        return;
      }

      try {
        const auctionItems = await this.fetchAuctionItems();
        const itemsToUpdate = auctionItems.filter((item) => {
          // Filter out items that are part of a live auction, have a Buy Now bid, or have already ended
          return (
            !(item.liveAuction && item.liveAuction === true) &&
            !(item.hasBuyNowBid && item.hasBuyNowBid === true) &&
            item.enddate.seconds * 1000 > Date.now()
          );
        });

        const batchPromises = itemsToUpdate.map(async (item) => {
          const itemRef = doc(db, "auctionItems", item.autoID);
          await updateDoc(itemRef, {
            enddate: this.selectedEndDate, // Set the new end date
          });
        });

        await Promise.all(batchPromises);
        alert("End time updated for all applicable items.");
      } catch (error) {
        console.error("Error updating end times:", error);
      }
    },
  },
};
</script>
